import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useQuery } from "@tanstack/react-query";
import { useUrlFriendlyToParameters } from 'helpers/urlFriendly';
import { getPdfs } from "data/Querys";
import MirsTable from 'components/general/table/Table.js';
import Label, { useLabels } from 'components/general/Label';
import Loader from "components/general/Loader";
import NotAvailable from "components/common/NotAvailable";
import { getLevelRSCPdf } from 'helpers/rsc';
import PageHeader from 'components/common/PageHeader';
import moment from 'moment';
import SelectorDiscipline from "components/common/selectors/selector-discipline/selectorDiscipline";
import SelectorEvent from "components/common/selectors/SelectorEvent";
import SelectorPdfType from 'components/common/selectors/selector-pdttype';
import { Stack } from "react-bootstrap";
import Competitor from 'components/presentation/Competitor';
import Noc from 'components/presentation/Noc';
import DateTime from 'components/presentation/DateTime';
import useViewport from 'components/general/useViewPort';
import { ALL_URL, SELECTOR_DISPLAY_TYPE, getDateTimeFormat } from 'helpers/utils';

import Sprites from "styles/images/SpriteIcons.svg";
import { ResetFilterButton } from 'components/common/ResetFilterButton';
import SvgMirs from 'components/presentation/SvgMirs';

export default function Reports() {
    const [dataState, setDataState] = useState([]);
    const [selectedPdfType, setSelectedPdfType] = useState('');
    const { mirs_competition, mirs_discipline, mirs_language, mirs_event } = useUrlFriendlyToParameters()
    const inputRef = useRef(null);
    const [pdfNameFilter, setPdfNameFilter] = useState();

    const { breakPoint } = useViewport();
    const isMobile = breakPoint === "sm" || breakPoint === "xs"

    const fLabel = useLabels()
    const placeholder = fLabel({ data: 'LabelSearch' });
    //Get Data
    const { data, isError, isLoading } = useQuery(
        {
            queryKey: ['pdfsTable', mirs_competition, mirs_language, mirs_discipline],
            queryFn: getPdfs,
            refetchInterval: 30000,
        });


    //posiciona al principio de la pásgina
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    //aplica los filtros 
    useEffect(() => {

        let filterData = data?.pdfs
        if (filterData && mirs_event && mirs_event !== ALL_URL.ALL_EVENT) {
            filterData = filterData?.filter(item => item?.eventUnit?.code.includes(mirs_event));
        }
        if (selectedPdfType && selectedPdfType !== ALL_URL.ALL_PDFs) {
            filterData = filterData?.filter(item => getLevelRSCPdf(item?.pdfCode, item.pdfType, item?.pdfSubcode) === selectedPdfType);
        }

        //filter GM and GN reports 
        filterData = filterData?.filter(data => (data.pdfType !== "GN" && data.pdfType !== "GM"));

        if (pdfNameFilter) {
            filterData = filterData?.filter(item =>
                item?.h1?.description?.toLowerCase().includes(pdfNameFilter?.toLowerCase())
                || item?.eventUnit?.schedule?.start?.at(0)?.participant?.name?.toLowerCase().includes(pdfNameFilter?.toLowerCase())
                || item?.eventUnit?.schedule?.start?.at(1)?.participant?.name?.toLowerCase().includes(pdfNameFilter?.toLowerCase())
                || item?.eventUnit?.schedule?.start?.at(0)?.organisation?.code?.toLowerCase().includes(pdfNameFilter?.toLowerCase())
                || item?.eventUnit?.schedule?.start?.at(1)?.organisation?.code?.toLowerCase().includes(pdfNameFilter?.toLowerCase())
                || item?.eventUnit?.longDescription?.toLowerCase().includes(pdfNameFilter?.toLowerCase())
            );
        };
        setDataState(filterData);
    }, [data, mirs_discipline, mirs_event, selectedPdfType, pdfNameFilter]);





    if (isError) { return <><NotAvailable label="LabelNoDataAvailable" /></>; }
    if (isLoading) { return <><Loader /></>; }

    const handleOnChange = event => {
        const { value } = event.target;
        setPdfNameFilter(value);
    };

    const onSelectPdfType = (type) => {
        setSelectedPdfType(type)
    }

    const clearFilterText = () => {
        if (inputRef?.current?.value) {
            inputRef.current.value = "";
        }
        setPdfNameFilter("");
    };

    const onClearFilters = () => {
        setSelectedPdfType('');
        clearFilterText();
    }

    return (
        <>
            <Stack direction={'horizontal'} className="selectors-container">
                <SelectorPdfType page="reports" onSelectPdfType={onSelectPdfType} selectedPdfType={selectedPdfType} />
                <SelectorDiscipline display_type={SELECTOR_DISPLAY_TYPE.COMBO} page="reports" displayAllDisc={false} />
                <SelectorEvent display_type={SELECTOR_DISPLAY_TYPE.COMBO} page="reports" />
                <form role="search" className="d-flex competitor-search">
                    <label htmlFor="PdfFilter" className="sr-only">Search for documents</label>
                    <input type="text" ref={inputRef} className="form-control" name="PdfFilter" id="PdfFilter" placeholder={placeholder} onChange={handleOnChange} />
                </form>
                <ResetFilterButton clearFilterText={clearFilterText} filterText={pdfNameFilter} page='reports' selectedPdfType={selectedPdfType} onClearFilters={onClearFilters} />
            </Stack>
            {dataState && <PdfsTable data={dataState} key={`PdfsTable`} />}

        </>
    )
};


function getColumns(fLabel) {
    return (
        [
            ColumnPdfType(fLabel),
            ColumnPdfName(),
            ColumnEvent({ label: "DocEvent" }),
            ColumnDateTime()
        ]
    )
}

function getColumnsSM(fLabel) {
    return (
        [
            ColumnPdfTypeEvent(fLabel),
            ColumnPdfName(),
            ColumnDateTime()
        ]
    )
}

function ColumnPdfTypeEvent(fLabel) {
    return (
        {
            Header: <Label data='DocTypeEvent' />,
            accessor: props => getLevelRSCPdf(props.pdfCode, props.pdfType, props.pdfSubcode),
            className: "text-start",
            headerClassName: "text-start",
            disableSortBy: false,
            id: 'PdfTypeEvent',
            Cell: props => {
                const row = props.cell.row.original;
                return (
                    <Stack direction="vertical">
                        <Stack>{getPdfType(props.value, fLabel)}</Stack>
                        <Stack>{row.pdfSubtitle || getPdfEvent(props)}</Stack>
                    </Stack>
                )
            }
        }
    )
}

function ColumnPdfType(fLabel) {

    return (
        {
            Header: <Label data={'DocType'} />,
            accessor: (props) => { const type = getLevelRSCPdf(props.pdfCode, props.pdfType, props.pdfSubcode); return getPdfType(type, fLabel) },
            className: "text-start",
            headerClassName: "text-start",
            disableSortBy: false,
            multiSort: [{ id: 'PdfType' }, { id: 'Event', desc: false }, { id: 'PdfDateTime', desc: true }],
            id: 'PdfType'
        }
    )
}




function ColumnPdfName() {
    return (
        {
            Header: <Label data={'DocName'} />,
            accessor: 'h1.description',
            className: "text-start",
            headerClassName: "text-start",
            disableSortBy: false,
            id: 'PdfEventUnit',
            Cell: (props) => {
                const row = props.cell.row.original;
                let out = "";
                if (row?.h1 == null || row?.h1?.description == "")
                    out = (row?.pdfTitle == "" || row?.pdfTitle == null) ? row?.pdfType : row?.pdfTitle;
                else
                    out = row?.h1?.description;
                const subcode = row.pdfSubcode;

                let typePdf = row.pdfType
                switch (getLevelRSCPdf(row.pdfCode, row.pdfType, row.pdfSubcode)) {
                    case "DATE":
                        const formatDate = getDateTimeFormat('DayMonthShort');
                        const dateTime = moment.tz(subcode, process.env.REACT_APP_TIME_ZONE)
                        out = <>
                            {out} <br /> <DateTime datetime={dateTime} format={formatDate} />
                        </>

                        break;
                    case "COMMUNI":
                        out = <>{out} - {subcode}</>
                        break;
                    case "SPORTCOMMUNI":
                        out = <>{out} - {subcode}</>
                        break;
                    default:
                        if (typePdf === 'C33' || typePdf === 'C84A') {
                            out = <>{out}</>
                        } else {
                            out = <>{out} {subcode && "-"} {subcode}</>
                        }
                        break;
                }
                out = <>
                    <a href={`${process.env.REACT_APP_SERVER_URL_RESOURCES}/pdf${row.fullPath}`} target="_blank" rel="noreferrer"  >
                        {out}<SvgMirs className='mirsIcon' title='LabelPdfFile' code='file-pdf' />
                    </a>
                </>

                return (out)
            }
        }
    )
}

function ColumnDateTime() {
    return (
        {
            Header: <Label data={'HeaderdocDate'} />,
            accessor: props => props?.pdfDate + props?.pdfTime?.slice(0, 6),
            className: "text-start",
            headerClassName: "text-start",
            disableSortBy: false,
            id: 'PdfDateTime',
            Cell: props => {
                const row = props.cell.row.original
                const formatDate = getDateTimeFormat('DayMonthShortHourMinute');
                const time = row?.pdfTime.slice(0, 2) + ":" + row?.pdfTime.slice(2, 4) + ":" + row?.pdfTime.slice(4, 6)
                const dateTime = row?.pdfDate + ' ' + time;
                const dateTime2 = moment.tz(dateTime, process.env.REACT_APP_TIME_ZONE)
                //const dateTime2 = moment(dateTime).tz(process.env.REACT_APP_TIME_ZONE)
                return (<DateTime datetime={dateTime2} format={formatDate} />)
            }
        }
    )

}

export function ColumnEvent({
    disableSort = false,
    label = "HeaderEvent"
}) {
    return (
        {
            Header: <Label data={label} legendFixed={true} />,
            accessor: 'eventUnit.longDescription',
            className: 'schedule-event',
            headerClassName: 'schedule-event',
            disableSortBy: disableSort,
            multiSort: [{ id: 'Event' }, { id: 'PdfDateTime', desc: true }],
            id: 'Event',
            Cell: (props) => {
                const row = props.cell.row.original;
                return (
                    <Stack direction="vertical">
                        <span>{row.pdfSubtitle || getPdfEvent(props)}</span>
                    </Stack>
                )
            }
        }
    )
}


const PdfsTable = ({ data }) => {
    const { breakPoint } = useViewport();
    const isMobile = breakPoint === "sm" || breakPoint === "xs"
    const fLabel = useLabels();

    const columns = useMemo(() => isMobile ? getColumnsSM(fLabel) : getColumns(fLabel)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , [isMobile]);

    return (
        <MirsTable
            className="table-schedule"
            id={'mirs-table-pdfs'}
            // data={data.pdfs}
            data={data}
            columns={columns}
            initialSortBy={[{ id: 'PdfDateTime', desc: true }]}
            showPagination={true}
        // pageSizeCustom={10}

        />
    )
}


const getPdfType = (props, fLabel) => {
    let lbl
    switch (props) {
        case "UNIT":
            lbl = fLabel({ data: 'EventUnitReports' })
            break;
        case "PHASE":
            lbl = fLabel({ data: 'PhaseReports' })
            break;
        case "EVENT":
            lbl = fLabel({ data: 'EventReports' })
            break;
        case "DISCIPLINE":
            lbl = fLabel({ data: 'DisciplineReports' })
            break;
        case "DATE":
            lbl = fLabel({ data: 'DateReports' })
            break;
        case "COMMUNI":
            lbl = fLabel({ data: 'LabelOfficialCommunications' })
            break;
        case "SEX":
            lbl = fLabel({ data: 'HeaderGender' })
            break;
        case "SPORTCOMMUNI":
            lbl = fLabel({ data: 'LabelSportCommunications' })
            break;
        default:
            lbl = props.value
            break;
    }
    return (lbl)
}

const getPdfEvent = props => {
    const row = props.cell.row.original
    let out
    let sw = Array.isArray(row?.eventUnit?.schedule?.start)

    switch (getLevelRSCPdf(row.pdfCode, row.pdfType, row.pdfSubcode)) {
        case "UNIT":
            out = <>
                <div>{row?.eventUnit?.longDescription}</div>
                {sw && <div className={'score-competitor-container  d-flex flex-row justify-content-start'}>
                    <div className='score-noc'>
                        <Noc className='me-1' {...row?.eventUnit?.schedule?.start?.at(0)?.participant?.organisation} />
                    </div>
                    <div className='score-name'><Competitor  {...row?.eventUnit?.schedule?.start.at(0)?.participant} hideNoc={true} onlyShortName={true} hidePicture={true} noLink={true} /> </div>
                </div>
                }
                {sw && <div className={'score-competitor-container  d-flex flex-row justify-content-start'}>
                    <div className='score-noc'>
                        <Noc className='me-1' {...row?.eventUnit?.schedule?.start?.at(1)?.participant?.organisation} />
                    </div>
                    <div className='score-name'><Competitor  {...row?.eventUnit?.schedule?.start?.at(1)?.participant} hideNoc={true} onlyShortName={true} hidePicture={true} noLink={true} /> </div>
                </div>
                }
                {row?.pdfParticipant && <div className={'score-competitor-container  d-flex flex-row justify-content-start'}>
                    <div className='score-noc'>
                        <Noc className='me-1' {...row?.pdfParticipant?.organisation} />
                    </div>
                    <div className='score-name'><Competitor  {...row?.pdfParticipant} hideNoc={true} hidePicture={true} noLink={true} /> </div>
                </div>
                }

            </>
            break;
        case "PHASE":
            out = <div>{row?.eventUnit?.longDescription}</div>
            break;
        case "EVENT":
            let subcode
            if (row?.pdfSubcode) {
                subcode = <>
                    {
                        row?.pdfTeam && <div className={'score-competitor-container  d-flex flex-row justify-content-start'}>
                            <div className='score-noc'>
                                <Noc className='me-1' {...row?.pdfTeam?.organisation} />
                            </div>
                            {/* <div className='score-name'> {row?.pdfTeam?.shortName}</div> */}
                            <div className='score-name'>
                                {
                                    (row?.pdfTeam?.teamType === 'ORG' && row?.pdfTeam?.organisation?.description !== null)
                                        ? row?.pdfTeam?.organisation?.description
                                        : row?.pdfTeam?.shortName
                                }
                            </div>
                        </div>
                    }

                    {
                        !row?.pdfTeam && row?.pdfParticipant && <div className={'score-competitor-container  d-flex flex-row justify-content-start'}>
                            <div className='score-noc'>
                                <Noc className='me-1' {...row?.pdfParticipant?.organisation} />
                            </div>
                            <div className='score-name'><Competitor  {...row?.pdfParticipant} hideNoc={true} hidePicture={true} noLink={true} /> </div>
                        </div>
                    }
                </>
            }
            out = <>
                <div>{row?.eventUnit?.longDescription}</div>
                {subcode}

            </>
            break;
        case "DISCIPLINE":
            out = <div>{row?.eventUnit?.longDescription}</div>
            break;
        case "DATE":
            out = <div>{row?.eventUnit?.longDescription}</div>
            break;
        case "COMMUNI":
            out = <div>{row?.eventUnit?.longDescription}</div>
            break;
        case "SPORTCOMMUNI":
            out = <div>{row?.eventUnit?.longDescription}</div>
            break;
        case "SEX":
            out = <div>{row?.eventUnit?.longDescription}</div>
            break;

        default:
            out = "Other"
            break;
    }
    return out
}


